import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

export interface AccessRestrictedProps {
  /**
   * Somewhere the user *can* go, when one exists. Rendered as the primary action so a refusal is a
   * junction rather than a dead end — the most common way to arrive here is a stale bookmark or a
   * link from a colleague with wider rights, and the useful next move is almost never "go back".
   */
  fallbackPath?: string;
  /** `common.json` key naming the fallback destination, e.g. `nav.logistics.inventory`. */
  fallbackLabelKey?: string;
}

/**
 * The one restricted state in the portal.
 *
 * <p>Before this existed, roughly twenty screens hand-rolled their own refusal: some rendered a bare
 * `ErrorState` with no page shell, some used the error-red styling of a crash for what is a routine
 * "not yours", and several read `canView` and then rendered the screen regardless. A refusal is not a
 * failure — nothing went wrong, the user simply asked for something outside their job — so it is
 * styled as a locked door, and it always offers a way onward.
 */
export function AccessRestricted({ fallbackPath, fallbackLabelKey }: AccessRestrictedProps) {
  const { t } = useTranslation('common');

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        py: 6,
        px: 3,
        minHeight: 240,
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} aria-hidden />
      <Typography variant="h6">{t('accessRestricted.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
        {t('accessRestricted.message')}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {fallbackPath ? (
          <Button variant="contained" component={RouterLink} to={fallbackPath}>
            {fallbackLabelKey
              ? t('accessRestricted.goTo', { section: t(fallbackLabelKey) })
              : t('accessRestricted.goToFallback')}
          </Button>
        ) : null}
        {/* Always available, permission-free by design: everyone may inspect their own access, and
            "what am I actually allowed to do?" is the question this screen provokes. */}
        <Button variant="outlined" component={RouterLink} to="/team/my-access">
          {t('accessRestricted.viewMyAccess')}
        </Button>
      </Stack>
    </Box>
  );
}
